import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { Waves, FileText, Coins, AlertTriangle, Ban, Percent, Mail, Shield, Gavel, Lock, Flame } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { PageHero } from "@/components/PageHero";

export default function Terms() {
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <AppHeader currentPage="terms" />
      
      <main className="flex-1 pt-20 pb-24 px-4">
        <div className="max-w-7xl mx-auto space-y-4">
          <PageHero
            title="Terms of Service"
            subtitle="Guidelines and rules for using Flow Marketplace"
          />

          {/* Content cards */}
          <div className="space-y-6">
            <Card className="bg-gradient-to-br from-primary/10 via-card to-card border-primary/20 hover:border-primary/40 transition-all duration-300 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row gap-4 sm:items-start">
                  <div className="p-3 rounded-xl gradient-primary flex-shrink-0">
                    <Waves className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-2">Platform Overview</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      Flow Marketplace is a decentralized NFT platform powered by Solana. Users can create, buy, sell, 
                      and earn EMON from digital assets. All transactions are executed via smart contracts for transparency and security.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-accent/10 via-card to-card border-accent/20 hover:border-accent/40 transition-all duration-300 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row gap-4 sm:items-start">
                  <div className="p-3 rounded-xl gradient-primary flex-shrink-0">
                    <Coins className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-2">EMON Token</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      EMON is a utility token used within the platform, not a security or investment vehicle. 
                      Use at your own risk — no guarantees of returns or platform uptime are provided.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* TOKEN ENFORCEMENT & PENALTIES - CRITICAL SECTION */}
            <Card className="bg-gradient-to-br from-destructive/10 via-card to-card border-destructive/20 hover:border-destructive/40 transition-all duration-300 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row gap-4 sm:items-start">
                  <div className="p-3 rounded-xl gradient-primary flex-shrink-0">
                    <Flame className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-2">Token Enforcement & Penalties</h2>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      By using the Flow platform, you acknowledge and agree that in cases of fraud, market manipulation, 
                      wash trading, abusive behavior, or violation of platform rules, Flow reserves the right to:
                    </p>
                    <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-4">
                      <li>Freeze accounts and associated NFTs</li>
                      <li>Remove NFTs from public visibility</li>
                      <li>Restrict access to marketplace features</li>
                      <li><strong className="text-foreground">Withhold, lock, or permanently burn EMON tokens</strong> associated with illicit activity</li>
                    </ul>
                    <p className="text-sm text-muted-foreground border-l-2 border-destructive/50 pl-4 italic">
                      Such actions may be executed automatically or manually and are <strong className="text-foreground">final and non-reversible</strong>. 
                      Users waive any right to claim compensation, refunds, or damages related to enforcement actions.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* AUTOMATED ENFORCEMENT DISCLAIMER */}
            <Card className="bg-gradient-to-br from-accent/10 via-card to-card border-accent/20 hover:border-accent/40 transition-all duration-300 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row gap-4 sm:items-start">
                  <div className="p-3 rounded-xl gradient-primary flex-shrink-0">
                    <Shield className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-2">Automated Enforcement Disclaimer</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      Flow uses automated systems to detect suspicious or abusive activity. 
                      Enforcement actions may occur <strong className="text-foreground">without prior notice</strong>. 
                      Flow is not responsible for losses resulting from enforcement triggered by system detection.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* NFT PRICING DISCLAIMER */}
            <Card className="bg-gradient-to-br from-primary/10 via-card to-card border-primary/20 hover:border-primary/40 transition-all duration-300 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row gap-4 sm:items-start">
                  <div className="p-3 rounded-xl gradient-primary flex-shrink-0">
                    <Gavel className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-2">NFT Pricing Disclaimer</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      NFT prices on Flow are determined by an <strong className="text-foreground">automatic bonding curve mechanism</strong> and 
                      do not represent intrinsic, artistic, or market value. Flow does not guarantee liquidity, demand, 
                      or future resale value of any NFT.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-destructive/10 via-card to-card border-destructive/20 hover:border-destructive/40 transition-all duration-300 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row gap-4 sm:items-start">
                  <div className="p-3 rounded-xl gradient-primary flex-shrink-0">
                    <Ban className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-2">Prohibited Actions</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      Fraudulent actions including wash trading, uploading inappropriate or copyrighted content, 
                      and market manipulation result in permanent ban and reward clawback. All transactions are monitored for suspicious activity.
                    </p>
                    <Link 
                      to="/anti-fraud" 
                      className="inline-flex items-center gap-2 mt-3 text-primary hover:text-primary/80 transition-colors text-sm font-medium"
                    >
                      <Shield className="w-4 h-4" />
                      Read our full Anti-Fraud Policy →
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-primary/10 via-card to-card border-primary/20 hover:border-primary/40 transition-all duration-300 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row gap-4 sm:items-start">
                  <div className="p-3 rounded-xl bg-gradient-to-br from-primary to-accent flex-shrink-0">
                    <Percent className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-2">Platform Fees</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      A platform fee applies to certain transactions. Fee structure is transparent and visible 
                      before any transaction confirmation. Fees help maintain platform operations and development.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-accent/10 via-card to-card border-accent/20 hover:border-accent/40 transition-all duration-300 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row gap-4 sm:items-start">
                  <div className="p-3 rounded-xl bg-gradient-to-br from-accent to-primary flex-shrink-0">
                    <AlertTriangle className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-2">Risk Disclaimer</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      Cryptocurrency and NFT transactions involve inherent risks. Market volatility, smart contract 
                      vulnerabilities, and regulatory changes may affect your assets. Only invest what you can afford to lose.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-primary/20 via-accent/10 to-card border-primary/30 hover:border-primary/50 transition-all duration-300 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row gap-4 sm:items-start overflow-hidden">
                  <div className="p-3 rounded-xl bg-gradient-to-br from-primary to-accent flex-shrink-0">
                    <Mail className="h-5 w-5 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2 className="text-xl font-semibold text-foreground mb-2">Contact & Agreement</h2>
                    <p className="text-muted-foreground leading-relaxed mb-3">
                      By using Flow Marketplace, you accept these terms. For questions or concerns about our terms of service:
                    </p>
                    <a 
                      href="mailto:support@emonboost.com" 
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/20 hover:bg-primary/30 text-primary font-medium transition-all duration-300 hover:scale-105 text-sm sm:text-base break-all"
                    >
                      <Mail className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate sm:overflow-visible">support@emonboost.com</span>
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
